import { useState } from 'react';
import { Sparkles, Wrench, Database, Tag } from 'lucide-react';
import { CHANGELOG, APP_VERSION } from '@uniticket/shared';
import type { ChangeKind } from '@uniticket/shared';

// « Nouveautés » — l'historique des versions, dans l'onglet Documentation (demande user 2026-08-31).
//
// Il lit CHANGELOG, qui est la SOURCE de la version affichée partout ailleurs (barre latérale,
// /health). C'est ce qui rend le système honnête : on ne peut pas publier une version sans écrire ce
// qu'elle contient, ni écrire une entrée sans que la version bouge. Avant, la constante de version
// vivait seule dans son coin et personne n'avait de raison d'y toucher — elle est restée à 1.0.0
// pendant sept déploiements.
//
// La version la plus récente est DÉPLIÉE, les précédentes repliées : on vient ici pour savoir ce qui
// vient de changer, l'archive n'est qu'un second usage.

const KIND: Record<ChangeKind, { icon: typeof Sparkles; label: string; cls: string }> = {
  feature: { icon: Sparkles, label: 'Nouveau', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  fix: { icon: Wrench, label: 'Correctif', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  // Les interventions sur les DONNÉES de production méritent leur propre couleur : ce ne sont ni des
  // fonctionnalités ni des correctifs de code, et ce sont celles qu'on cherche des mois plus tard.
  data: { icon: Database, label: 'Données', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const frDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' });

export function Changelog() {
  const [open, setOpen] = useState<string[]>([CHANGELOG[0].version]);
  const toggle = (v: string) => setOpen((o) => (o.includes(v) ? o.filter((x) => x !== v) : [...o, v]));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="text-sm text-slate-600">
          Ce que l’application a gagné, version par version.
        </p>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-rose/25 bg-brand-rose/[0.06] px-2.5 py-1 text-xs font-semibold text-brand-rose">
          <Tag className="h-3.5 w-3.5" />
          version en service : {APP_VERSION}
        </span>
      </div>

      <div className="space-y-2">
        {CHANGELOG.map((rel, i) => {
          const isOpen = open.includes(rel.version);
          const latest = i === 0;
          return (
            <section
              key={rel.version}
              className={`overflow-hidden rounded-xl border ${latest ? 'border-brand-rose/30 bg-brand-rose/[0.03]' : 'border-slate-200 bg-white'}`}
            >
              <button
                type="button"
                onClick={() => toggle(rel.version)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/60"
              >
                <span className={`shrink-0 rounded-md px-2 py-1 font-mono text-xs font-bold ${
                  latest ? 'bg-brand-rose text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {rel.version}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-800">{rel.title}</span>
                  <span className="block text-xs text-slate-500">
                    {frDate(rel.date)} · {rel.items.length} changement{rel.items.length > 1 ? 's' : ''}
                    {latest && <span className="ml-2 font-semibold text-brand-rose">version actuelle</span>}
                  </span>
                </span>
                <span className="shrink-0 text-slate-400">{isOpen ? '▾' : '▸'}</span>
              </button>

              {isOpen && (
                <ul className="space-y-2.5 border-t border-slate-100 px-4 py-3">
                  {rel.items.map((it, j) => {
                    const k = KIND[it.kind];
                    const Icon = k.icon;
                    return (
                      <li key={j} className="flex gap-2.5">
                        <span className={`mt-0.5 inline-flex h-5 shrink-0 items-center gap-1 rounded border px-1.5 text-[10px] font-semibold ${k.cls}`}>
                          <Icon className="h-3 w-3" />
                          {k.label}
                        </span>
                        <span className="min-w-0 text-[13px] leading-relaxed text-slate-700">
                          <span className="font-semibold text-slate-800">{it.area}</span>
                          <span className="mx-1.5 text-slate-300">·</span>
                          {it.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      <p className="mt-4 text-[11px] text-slate-400">
        Les dates sont celles de la mise en production. La version affichée dans la barre latérale et
        renvoyée par le contrôle de santé est celle de l’entrée la plus récente de cette page.
      </p>
    </div>
  );
}
